import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { Component, ReactNode } from 'react';

const preloadErrors = new WeakSet<object>();
const reloadStorageKey = 'deployment-update:last-reload';
const reloadVersionKey = 'deployment-update:last-reload-version';
const reloadCooldownMs = 60_000;
let preloadFailed = false;
let reloadRequested = false;
const isOffline = () => navigator.onLine === false;

function reloadPage(automatic: boolean, version: string) {
  if (isOffline() || reloadRequested) return;
  if (automatic) {
    // Limit recovery to one automatic reload per target build and browser tab.
    try {
      const lastReload = Number(window.sessionStorage.getItem(reloadStorageKey));
      const now = Date.now();
      if (window.sessionStorage.getItem(reloadVersionKey) === version) return;
      if (lastReload && now - lastReload < reloadCooldownMs) return;
      window.sessionStorage.setItem(reloadStorageKey, String(now));
      window.sessionStorage.setItem(reloadVersionKey, version);
    } catch {
      // Keep the explicit retry available when browser storage is blocked.
      return;
    }
  }
  reloadRequested = true;
  window.location.reload();
}

// Remember failures even when the first route fails before React mounts.
// Let React receive the original error so its boundary can show a fallback.
window.addEventListener('vite:preloadError', (event) => {
  const { payload } = event as Event & { payload?: unknown };
  if (payload && typeof payload === 'object') preloadErrors.add(payload);
  preloadFailed = true;
});

type Props = { children: ReactNode };
type State = {
  updateAvailable: boolean;
  importFailed: boolean;
  loadFailed: boolean;
  offline: boolean;
  checking: boolean;
  connectionFailed: boolean;
};

export default class DeploymentUpdateBoundary extends Component<Props, State> {
  private versionInterval?: number;

  private versionRequest?: AbortController;

  private mounted = false;

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (!preloadErrors.has(error)) throw error;
    return { loadFailed: true, importFailed: true };
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      updateAvailable: false,
      importFailed: false,
      loadFailed: preloadFailed,
      offline: isOffline(),
      checking: false,
      connectionFailed: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    window.addEventListener('vite:preloadError', this.handlePreloadError);
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    if (import.meta.env.PROD) {
      this.checkVersion();
      this.versionInterval = window.setInterval(this.handleVersionCheck, 60_000);
      document.addEventListener('visibilitychange', this.handleVersionCheck);
      window.addEventListener('focus', this.handleVersionCheck);
    }
  }

  componentDidCatch() {
    this.checkVersion();
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('vite:preloadError', this.handlePreloadError);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.clearInterval(this.versionInterval);
    document.removeEventListener('visibilitychange', this.handleVersionCheck);
    window.removeEventListener('focus', this.handleVersionCheck);
    this.versionRequest?.abort();
  }

  private checkVersion = async (manual = false) => {
    const { updateAvailable, loadFailed } = this.state;
    if (
      !this.mounted ||
      this.versionRequest ||
      (!manual && document.visibilityState !== 'visible') ||
      (!manual && updateAvailable && !loadFailed)
    ) return;

    if (isOffline()) {
      this.setState({ offline: true });
      return;
    }

    const controller = new AbortController();
    this.versionRequest = controller;
    this.setState({ checking: true });
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}version.json`, {
        cache: 'no-store',
        signal: controller.signal,
        redirect: 'error',
      });
      if (!response.ok) throw new Error('Version check unavailable');
      const data: unknown = await response.json();
      if (
        !data ||
        typeof data !== 'object' ||
        !('version' in data) ||
        typeof data.version !== 'string' ||
        !data.version.trim()
      ) throw new Error('Invalid version metadata');
      if (!this.mounted || controller.signal.aborted || isOffline()) return;

      const changed = data.version !== import.meta.env.VITE_BUILD_ID;
      const { loadFailed: currentLoadFailed } = this.state;
      this.setState({ updateAvailable: changed, offline: false, connectionFailed: false });
      // Automatic recovery requires both a failed import and a confirmed new build.
      // Explicit retries may reload the same build, since React.lazy caches rejection.
      if (manual || (changed && currentLoadFailed)) reloadPage(!manual, data.version);
    } catch {
      if (this.mounted && this.versionRequest === controller) {
        this.setState((previous) => ({
          connectionFailed: true,
          offline: isOffline(),
          loadFailed: manual || previous.loadFailed,
        }));
      }
    } finally {
      clearTimeout(timeout);
      if (this.versionRequest === controller) {
        this.versionRequest = undefined;
        if (this.mounted) this.setState({ checking: false });
      }
    }
  };

  private handleVersionCheck = () => {
    this.checkVersion();
  };

  private handleOnline = () => {
    this.setState({ offline: false }, this.handleVersionCheck);
  };

  private handleOffline = () => {
    this.versionRequest?.abort();
    this.versionRequest = undefined;
    this.setState({ offline: true, checking: false });
  };

  private handlePreloadError = () => {
    this.setState({ loadFailed: true }, this.handleVersionCheck);
  };

  private handleRetry = () => {
    this.checkVersion(true);
  };

  render() {
    const { children } = this.props;
    const { updateAvailable, importFailed, loadFailed, offline, checking, connectionFailed } = this.state;
    const unavailable = offline || connectionFailed;
    const buttonLabel = updateAvailable && !unavailable ? 'Atualizar página' : 'Tentar novamente';
    let message = 'Atualize a página para usar a nova versão. Alterações não salvas serão perdidas.';
    if (offline) message = 'Você está sem conexão. Reconecte-se para tentar novamente.';
    else if (connectionFailed) message = 'Não foi possível acessar o painel. Aguarde um momento e tente novamente.';
    else if (loadFailed && !updateAvailable) message = 'Não foi possível carregar esta página. Tente novamente.';

    return (
      <>
        {!importFailed && children}
        <Snackbar open={updateAvailable || loadFailed} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity={loadFailed || unavailable ? 'warning' : 'info'}
            variant="filled"
            sx={{ width: '100%', maxWidth: 600, '& .MuiAlert-action': { alignItems: 'center' } }}
            action={
              <Button color="inherit" variant="outlined" size="small" disabled={checking || offline} onClick={this.handleRetry}>
                {checking ? 'Verificando...' : buttonLabel}
              </Button>
            }
          >
            <AlertTitle>
              {updateAvailable && !unavailable ? 'Nova versão disponível' : 'Falha ao carregar o painel'}
            </AlertTitle>
            {message}
          </Alert>
        </Snackbar>
      </>
    );
  }
}
